import React, { useState, useEffect } from 'react';
import ModalHeader from '../components/ModalHeader';
import useAuth from '../hooks/useAuth';
import { getAllUsers, createUser, deleteUser } from '../api';
import { Link } from 'react-router-dom';
import { BsArrowLeftShort } from 'react-icons/bs';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    Alert, 
    Snackbar,
    Chip,
    Box,
    IconButton,
    Tooltip
} from '@mui/material';
import { MdAdd, MdPerson, MdDelete } from 'react-icons/md';

const colors = {
    app_brown: '#f07e64',
    app_red: '#e35972',
    app_green: '#6fa84a',
    app_green_light: '#93BC78',
    app_yellow: '#f2c237',
    app_blue: '#49a3d4',
    app_blue_dark: '#136492',
    app_dark_green: '#098C5E',
};

const userTypes = [
    { value: 'admin', label: 'Administrator', color: '#e35972' },
    { value: 'user1', label: 'User Type 1', color: '#6fa84a' },
    { value: 'user2', label: 'User Type 2', color: '#49a3d4' },
    { value: 'user3', label: 'User Type 3', color: '#f2c237' }
];

function Users() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        password: '',
        type: 'user1'
    });
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        const fetchUsersOnMount = async () => {
            try {
                const users = await getAllUsers();
                setUsers(users);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching users:', error);
                showAlert('Σφάλμα κατά τη φόρτωση των χρηστών', 'error');
                setLoading(false);
            }
        };
        
        fetchUsersOnMount();
    }, []);

    const fetchUsers = async () => {
        try {
            const users = await getAllUsers();
            setUsers(users);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            showAlert('Σφάλμα κατά τη φόρτωση των χρηστών', 'error');
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        try {
            await createUser(newUser);
            
            showAlert('Ο χρήστης δημιουργήθηκε επιτυχώς!', 'success');
            setOpenDialog(false);
            setNewUser({ name: '', email: '', password: '', type: 'user1' });
            fetchUsers(); // Refresh the list
        } catch (error) {
            console.error('Error creating user:', error);
            const errorMessage = error.response?.data?.error || error.error || 'Σφάλμα κατά τη δημιουργία χρήστη';
            showAlert(errorMessage, 'error');
        }
    };

    const handleDeleteClick = (userItem) => {
        setUserToDelete(userItem);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteUser(userToDelete.id);
            showAlert('Ο χρήστης διαγράφηκε επιτυχώς!', 'success');
            setOpenDeleteDialog(false);
            setUserToDelete(null);
            fetchUsers(); // Refresh the list
        } catch (error) {
            console.error('Error deleting user:', error);
            const errorMessage = error.response?.data?.error || error.error || 'Σφάλμα κατά τη διαγραφή χρήστη';
            showAlert(errorMessage, 'error');
        }
    };

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
        setUserToDelete(null);
    };

    const showAlert = (message, severity) => {
        setAlert({ open: true, message, severity });
    };

    const getUserTypeLabel = (type) => {
        const userType = userTypes.find(ut => ut.value === type);
        return userType ? userType.label : type;
    };

    const getUserTypeColor = (type) => {
        const userType = userTypes.find(ut => ut.value === type);
        return userType ? userType.color : '#757575';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('el-GR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if user has permission to access this page
    if (!user || user.role !== 'admin') {
        return (
            <div className="flex flex-col justify-center items-center min-h-screen bg-white">
                <Alert severity="error" sx={{ mb: 2 }}>
                    Δεν έχετε δικαίωμα πρόσβασης σε αυτή τη σελίδα. Μόνο διαχειριστές επιτρέπονται.
                </Alert>
            </div>
        );
    }

    return (
        <div className="flex flex-col justify-between bg-white min-h-screen">
            <ModalHeader title="Διαχείριση Χρηστών" subtitle="Δημιουργία και διαχείριση λογαριασμών χρηστών" />
            
            <div className='items-center justify-between mx-auto mb-2 -mt-2 lg:flex lg:px-0'>
                <Link to={"/select"} className="flex text-app_green_light shadow-md font-bold rounded-full text-lg px-10 py-2 text-center">
                    <BsArrowLeftShort className='self-center w-6 h-6 text-app_green_light' /> Επιστροφή
                </Link>
            </div>

            <div className="items-center justify-between px-4 mx-auto lg:flex lg:px-0 max-w-[1200px] mb-8 mt-3">
                <Button
                    variant="contained"
                    startIcon={<MdAdd />}
                    onClick={() => setOpenDialog(true)}
                    sx={{
                        backgroundColor: colors.app_green,
                        '&:hover': { backgroundColor: colors.app_dark_green },
                        fontWeight: 'bold',
                        borderRadius: '25px',
                        px: 3,
                        py: 1,
                        textTransform: 'none'
                    }}
                >
                    Δημιουργία Νέου Χρήστη
                </Button>
            </div>

            <div className="flex-1 px-4 mx-auto max-w-[1200px] w-full">
                <Box sx={{ mb: 3 }}>
                    <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: colors.app_blue }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        <MdPerson className="inline mr-2" />
                                        Όνομα
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Τύπος Χρήστη</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ημερομηνία Δημιουργίας</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ενέργειες</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">Φόρτωση...</TableCell>
                                    </TableRow>
                                ) : users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">Δεν βρέθηκαν χρήστες</TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((userItem) => (
                                        <TableRow key={userItem.id} hover>
                                            <TableCell>{userItem.name}</TableCell>
                                            <TableCell>{userItem.email}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={getUserTypeLabel(userItem.type)}
                                                    sx={{ 
                                                        backgroundColor: getUserTypeColor(userItem.type),
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{formatDate(userItem.createdAt)}</TableCell>
                                            <TableCell>
                                                {userItem.type !== 'admin' && (
                                                    <Tooltip title="Διαγραφή χρήστη">
                                                        <IconButton
                                                            onClick={() => handleDeleteClick(userItem)}
                                                            sx={{ 
                                                                color: colors.app_red,
                                                                '&:hover': { 
                                                                    backgroundColor: `${colors.app_red}20` 
                                                                }
                                                            }}
                                                        >
                                                            <MdDelete />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </div>

            {/* Create User Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ 
                    backgroundColor: colors.app_blue, 
                    color: 'white',
                    fontWeight: 'bold'
                }}>
                    Δημιουργία Νέου Χρήστη
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Όνομα"
                        fullWidth
                        variant="outlined"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Email"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Κωδικός Πρόσβασης"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Τύπος Χρήστη</InputLabel>
                        <Select
                            value={newUser.type}
                            onChange={(e) => setNewUser({ ...newUser, type: e.target.value })}
                            label="Τύπος Χρήστη"
                        >
                            {userTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    <Chip 
                                        label={type.label}
                                        sx={{ 
                                            backgroundColor: type.color,
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                        size="small"
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={() => setOpenDialog(false)}
                        variant="outlined"
                        sx={{ mr: 1, textTransform: 'none' }}
                    >
                        Ακύρωση
                    </Button>
                    <Button 
                        onClick={handleCreateUser}
                        variant="contained"
                        sx={{
                            backgroundColor: colors.app_green,
                            '&:hover': { backgroundColor: colors.app_dark_green },
                            textTransform: 'none'
                        }}
                        disabled={!newUser.name || !newUser.email || !newUser.password}
                    >
                        Δημιουργία
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ 
                    backgroundColor: colors.app_red, 
                    color: 'white',
                    fontWeight: 'bold'
                }}>
                    Επιβεβαίωση Διαγραφής
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη;
                    </Alert>
                    {userToDelete && (
                        <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <strong>Όνομα:</strong> {userToDelete.name}<br />
                            <strong>Email:</strong> {userToDelete.email}<br />
                            <strong>Τύπος:</strong> {getUserTypeLabel(userToDelete.type)}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        sx={{ mr: 1, textTransform: 'none' }}
                    >
                        Ακύρωση
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{
                            backgroundColor: colors.app_red,
                            '&:hover': { backgroundColor: '#d32f2f' },
                            textTransform: 'none'
                        }}
                    >
                        Διαγραφή
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setAlert({ ...alert, open: false })} 
                    severity={alert.severity}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* <div className="bottom-0 w-full h-5 bg-gradient-to-r from-app_green to-app_blue" /> */}
        </div>
    );
}

export default Users;
